import $ from 'jquery';
import Backbone from 'backbone';
import { T_ } from '@shared/translation';
import {
isValidEmail,
isBlockedEmail,
isValidUsernameFormat } from
'@shared/utility-functions';
import { tagsSessionId } from '@shared/threatmetrix';
import { getCSRFToken } from '@shared/cookie';

/*
                                                * Signup model
                                                */
export default Backbone.Model.extend({
  defaults: {
    email: '',
    username: '',
    password: '',
    confirmpwd: '',
    looking_for: 'Both',
    coupon: '',
    thm_session: '',
    signup_skip_captcha: '',
    proj_id: '', // Local Jobs
    project_collaboration_invitation_token: '',
    contest_collaboration_invitation_token: '',
    thread_id: '',
    validating_signup: false },


  isValidEmail(override = false) {
    if (this.get('validating_signup') && !override) {
      return;
    }
    return this.validateEmail(this.get('email')).then(
    () => {
      this.trigger('email:valid');
      return true;
    },
    rejectMsg => {
      this.trigger('email:invalid', rejectMsg);
      return false;
    });

  },

  isValidUsername(override = false) {
    if (this.get('validating_signup') && !override) {
      return;
    }
    return this.validateUsername(this.get('username')).then(
    () => {
      this.trigger('username:valid');
      return true;
    },
    rejectMsg => {
      this.trigger('username:invalid', rejectMsg);
      return false;
    });

  },

  isValidPassword(override = false) {
    if (this.get('validating_signup') && !override) {
      return;
    }
    return this.validatePassword(
    this.get('password'),
    this.get('username'),
    this.get('email')).
    then(
    () => {
      this.trigger('password:valid');
      return true;
    },
    rejectMsg => {
      this.trigger('password:invalid', rejectMsg);
      return false;
    });

  },

  isValidConfirmpwd(override = false) {
    if (this.get('validating_signup') && !override) {
      return;
    }
    return this.validateConfirmPwd(
    this.get('password'),
    this.get('confirmpwd')).
    then(
    () => {
      this.trigger('confirmpwd:valid');
      return true;
    },
    rejectMsg => {
      this.trigger('confirmpwd:invalid', rejectMsg);
      return false;
    });

  },

  isValidLookingFor(override = false) {
    if (this.get('validating_signup') && !override) {
      return;
    }
    return this.validateLookingFor(this.get('looking_for')).then(
    () => {
      this.trigger('looking_for:valid');
      return true;
    },
    rejectMsg => {
      this.trigger('looking_for:invalid', rejectMsg);
      return false;
    });

  },

  isEmployer() {
    return this.get('looking_for') == 'Employer';
  },

  /**
      * Returns promise to validate Email based on:
      * - Format
      * - Uniqueness: Not in use already
      * - Not Blocked
      */
  validateEmail(email) {
    const emailValidFormat = (resolve, reject) => {
      if (!email) {
        return reject(T_('กรุณาใส่อีเมล'));
      }
      if (!isValidEmail(email)) {
        return reject(T_`กรุณาระบุอีเมลที่ถูกต้อง`);
      }
      return resolve(true);
    };

    const emailUnique = (resolve, reject) => {
      const encodedEmail = encodeURIComponent(email);
      // Check whether email exists
      fetch(
      `/ajax/check-email-exist.php?email=${encodedEmail}&json=true&validator=true`).

      then(res => res.text()).
      then(res => {
        if (res !== 'true') {
          return reject(T_`This email address is already in use.`);
        }
        return resolve(true);
      }).
      catch(() => reject(true));
    };

    const emailNotBlocked = (resolve, reject) => {
      isBlockedEmail(email).
      then(result => {
        if (result == false) {
          return resolve(true);
        }
        return reject(
        T_`This email domain is blocked. Please use a different email address.`);

      }).
      catch(() => reject(T_`Server error, please try again`));
    };

    return new Promise(emailValidFormat).
    then(() => new Promise(emailNotBlocked)).
    then(() => new Promise(emailUnique));
  },

  /**
      * Returns promise to validate Username based on:
      * - Format
      * - Uniqueness: Not in use already
      */
  validateUsername(username) {
    const usernameValidFormat = new Promise((resolve, reject) => {
      if (!username) {
        return reject(T_('กรุณาระบุชื่อผู้ใช้'));
      }
      // Test length
      const length = username.length;
      if (length < 3 || length > 16) {
        return reject(T_`ชื่อผู้ใช้ต้องประกอบด้วย 3-16 ตัวอักษร`);
      }
      // Test format
      if (!isValidUsernameFormat(username)) {
        return reject(T_`ชื่อผู้ใช้ต้องเป็นตัวอักษรหรือตัวอักษรและตัวเลขโดยขึ้นต้นด้วยตัวอักษร`);
      }
      return resolve(true);
    });

    const usernameUnique = new Promise((resolve, reject) => {
      const encodedUsername = encodeURIComponent(username);
      // Check whether username exists
      fetch(
      `/ajax/check-username-exist.php?username=${encodedUsername}&json=true&validator=true`).

      then(res => res.text()).
      then(res => {
        if (res !== 'true') {
          return reject(T_`ชื่อผู้ใช้นี้มีอยู่แล้วในระบบ กรุณาเลือกชื่ออื่น`);


        }
        return resolve(true);
      }).
      catch(() => reject(true));
    });

    return Promise.all([usernameValidFormat, usernameUnique]);
  },

  /**
      * Returns promise to validate password based on:
      * Move to use utility functions : is valid password format?
      */
  validatePassword(pwd, username, email) {
    return new Promise((resolve, reject) => {
      if (!pwd) {
        return reject(T_('กรุณาระบุรหัสผ่าน'));
      }
      // Test format
      const re = /^.{6,}$/i;
      if (!re.test(pwd)) {
        return reject(T_`รหัสผ่านต้องประกอบด้วยอย่างน้อย 6 ตัวอักษร`);
      }
      // Test strength
      let strength = 0;
      strength += pwd.match(/\d/g) ? 2 : 0;
      strength += pwd.match(/[A-Z]/g) ? 4 : 0;
      strength += pwd.match(/[a-z]/g) ? 4 : 0;
      if (strength <= 4) {
        return reject(T_`กรุณาระบุรหัสผ่านที่มีความปลอดภัยและแข็งแกร่ง`);
      }
      if (pwd == username) {
        return reject(T_`รหัสผ่านต้องแตกต่างจากชื่อผู้ใช้`);
      }
      if (pwd == email) {
        return reject(T_`Password must be different to email`);
      }
      return resolve(true);
    });
  },

  validateConfirmPwd(password, confirmPassword) {
    return new Promise((resolve, reject) => {
      if (!confirmPassword) {
        return reject(T_('กรุณาระบุรหัสผ่านของคุณอีกครั้ง'));
      }
      if (password != confirmPassword) {
        return reject(T_('รหัสผ่านไม่ตรงกัน!'));
      }
      return resolve(true);
    });
  },

  validateLookingFor(lookingFor) {
    return new Promise((resolve, reject) => {
      if (!lookingFor) {
        return reject(T_('กรุณาเลือกบทบาท'));
      }
      return resolve(true);
    });
  },

  /**
      * Validates all the Elements for Signup
      * Chains Promises
      */
  validateAll() {
    return new Promise((resolve, reject) => {
      let valid = true;

      this.isValidEmail(true).
      then(result => {
        valid &= result;
        return this.isValidUsername(true);
      }).
      then(result => {
        valid &= result;
        return this.isValidPassword(true);
      }).
      then(result => {
        valid &= result;
        return this.isValidConfirmpwd(true);
      }).
      then(result => {
        valid &= result;
        return this.isValidLookingFor(true);
      }).
      then(result => {
        valid &= result;
      }).
      then(() => {
        if (!valid) {
          return reject(true);
        }
        return resolve(true);
      }).
      catch(() => reject(true));
    });
  },

  signup() {
    // Activate Signup Mode. Prevents other outside event triggers from updating
    // error messages.
    this.set({ validating_signup: true });

    // Validate the model first
    this.validateAll().
    then(() => {
      this.sendSignupData();
    }).
    catch(() => {
      // Validation Rejected
      this.set({ validating_signup: false });
    });
  },

  sendSignupData() {
    // Send POST request
    $.ajax({
      url: '/users/onsignup.php',
      context: this,
      type: 'POST',
      cache: false,
      data: {
        email: this.get('email'),
        newusername: this.get('username'),
        newuserpasswd: this.get('password'),
        newuserpasswd1: this.get('confirmpwd'),
        looking_for: this.get('looking_for'),
        coupon: this.get('coupon'),
        newsignup: true,
        ajax: true,
        inline_signup: true,
        captcha_breakpoint_id: this.get('captcha_breakpoint_id'),
        captcha_breakpoint_token: this.get('captcha_breakpoint_token'),
        page: window.location.pathname,
        thm_session: tagsSessionId,
        signup_skip_captcha: this.get('signup_skip_captcha'),
        proj_id: this.get('proj_id'), // Local Jobs: Add project id
        project_collaboration_invitation_token: this.get(
        'project_collaboration_invitation_token'),

        contest_collaboration_invitation_token: this.get(
        'contest_collaboration_invitation_token'),

        thread_id: this.get('thread_id'),
        csrf_token: getCSRFToken() },

      dataType: 'JSON',
      success(resp) {
        if (resp.status == 'success') {
          this.trigger('success', { redirectUrl: resp.goto_url });
        } else if (
        resp.captcha_breakpoint_id &&
        resp.captcha_breakpoint_token &&
        resp.public_key)
        {
          this.trigger('captcha', {
            signupModel: this,
            captcha_breakpoint_id: resp.captcha_breakpoint_id,
            captcha_breakpoint_token: resp.captcha_breakpoint_token,
            public_key: resp.public_key });

          this.set({ validating_signup: false });
        } else if (resp.reason) {
          this.trigger('error', resp.reason);
          this.set({ validating_signup: false });
        } else {
          this.trigger('error', T_('ข้อผิดพลาดภายใน'));
          this.set({ validating_signup: false });
        }
      },
      error() {
        this.set({ validating_signup: false });
        this.trigger('error', T_('ข้อผิดพลาด: ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้'));
      } });

  } });